import React, { useState } from 'react';
import { Box, Container, Typography, Grid, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { styled } from '@mui/system';
import { getBlogPosts, getCategoryById } from '../data/blogData';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArticleIcon from '@mui/icons-material/Article';
import { useTenantConfig } from '../hooks/useTenant';

const S = { main: '#0891b2', light: '#22d3ee', dark: '#0e7490' };

/* Section badge (pill) */
const SectionBadge = styled(motion.div)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 20px',
  borderRadius: 50,
  background: 'rgba(255,255,255,0.9)',
  backdropFilter: 'blur(12px)',
  border: `1px solid ${alpha('#0891b2', 0.15)}`,
  boxShadow: '0 2px 12px rgba(8,145,178,0.08)',
  marginBottom: 20,
});

// Blog Kartı
const BlogCard = ({ post, index, featured = false }) => {
  const category = getCategoryById(post.category);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      viewport={{ once: true }}
      style={{ height: '100%' }}
    >
      <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
        <Box
          sx={{
            height: '100%',
            borderRadius: 3,
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.06)',
            overflow: 'hidden',
            transition: 'all 0.25s ease',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: '0 12px 24px rgba(0,0,0,0.06)',
              '& .blog-arrow': {
                transform: 'translateX(4px)',
                color: S.main,
              },
              '& .blog-image': {
                transform: 'scale(1.03)',
              },
            },
          }}
        >
          {/* Image */}
          <Box
            sx={{
              height: featured ? 200 : 160,
              background: `linear-gradient(135deg, ${alpha(S.main, 0.1)} 0%, ${alpha('#f59e0b', 0.1)} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box
              component={'img'}
              src={post.image}
              className="blog-image"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.4s ease',
              }}
            />
            {/* Category Badge */}
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                left: 12,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(8px)',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: S.main,
              }}
            >
              {category?.name}
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Title */}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: featured ? '1.1rem' : '1rem',
                color: '#0f172a',
                mb: 1,
                lineHeight: 1.4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {post.title}
            </Typography>

            {/* Excerpt */}
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                mb: 2,
                flex: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {post.excerpt}
            </Typography>

            {/* Meta */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 2,
                borderTop: '1px solid #f1f5f9',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#94a3b8' }}>
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '0.75rem' }}>{post.readTime}</Typography>
              </Box>
              <Box
                className="blog-arrow"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: '#94a3b8',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  transition: 'all 0.25s ease',
                }}
              >
                Devamını Oku
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </motion.div>
  );
};

// Kategori Chip
const CategoryChip = ({ category, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 2,
      py: 1,
      borderRadius: 2,
      fontSize: '0.85rem',
      fontWeight: 600,
      background: selected ? S.main : '#fff',
      color: selected ? '#fff' : '#64748b',
      border: selected ? 'none' : '1px solid rgba(0,0,0,0.06)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      '&:hover': {
        background: selected ? S.main : '#f1f5f9',
        transform: 'translateY(-2px)',
      },
    }}
  >
    {category.emoji} {category.name}
  </Box>
);

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { tenantName } = useTenantConfig();

  const blogPosts = getBlogPosts(tenantName);
  const filteredPosts = selectedCategory
    ? blogPosts.filter(post => post.category === selectedCategory)
    : blogPosts;

  const featuredPosts = blogPosts.filter(post => post.featured).slice(0, 2);

  return (
    <Box sx={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: 6,
          pb: 6,
          background: '#fff',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
              <SectionBadge
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <ArticleIcon sx={{ fontSize: 16, color: S.main }} />
                <Typography sx={{
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: '#334155',
                }}>
                  BLOG
                </Typography>
              </SectionBadge>

              <Typography
                sx={{
                  fontSize: { xs: '1.8rem', md: '2.4rem' },
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  mb: 2,
                }}
              >
                Dernek Yönetim{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(135deg, ${S.main} 0%, ${S.dark} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Rehberi
                </Box>
              </Typography>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '1rem',
                  lineHeight: 1.75,
                }}
              >
                Dernek yönetimi, dijitalleşme ve verimlilik konularında
                rehber içerikler ve uzman önerileri.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Featured Posts */}
        {!selectedCategory && featuredPosts.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box
                sx={{
                  width: 4,
                  height: 24,
                  borderRadius: 1,
                  background: `linear-gradient(180deg, ${S.main} 0%, ${S.dark} 100%)`,
                }}
              />
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: '#0f172a',
                }}
              >
                Öne Çıkan Yazılar
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {featuredPosts.map((post, index) => (
                <Grid item xs={12} md={6} key={post.id}>
                  <BlogCard post={post} index={index} featured />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* All Posts */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 4,
                height: 24,
                borderRadius: 1,
                background: `linear-gradient(180deg, ${S.main} 0%, ${S.dark} 100%)`,
              }}
            />
            <Typography
              sx={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: '#0f172a',
              }}
            >
              {selectedCategory 
                ? getCategoryById(selectedCategory)?.name 
                : 'Tüm Yazılar'}
              <Box component="span" sx={{ color: '#94a3b8', fontWeight: 500, ml: 1, fontSize: '0.9rem' }}>
                ({filteredPosts.length})
              </Box>
            </Typography>
          </Box>

          <Grid container spacing={2.5}>
            {filteredPosts.map((post, index) => (
              <Grid item xs={12} sm={6} md={4} key={post.id}>
                <BlogCard post={post} index={index} />
              </Grid>
            ))}
          </Grid>

          {filteredPosts.length === 0 && (
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 8,
                px: 3,
                borderRadius: 3,
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Typography sx={{ color: '#94a3b8', fontSize: '1rem' }}>
                Bu kategoride henüz yazı bulunmuyor.
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPage;
